import { LoaderCircle } from 'lucide-react';
import { ReactNode } from 'react';

import {
  getFormattedDate,
  getPriorityClassName,
  getPriorityLabel,
  getStatusClassName,
  getStatusFromError,
  getStatusLabel,
  TicketsWorkspaceText,
} from '../utils';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { TicketSummary } from '@/services/TicketsService';

interface TicketListContentProps {
  isLoading: boolean;
  ticketsError: unknown;
  tickets: TicketSummary[];
  selectedTicketId: number | null;
  isAdminVariant: boolean;
  dateLocale: string;
  text: TicketsWorkspaceText;
  onRetry: () => void;
  onSelectTicket: (ticketId: number) => void;
  renderTicketOwnership: (
    ticket: TicketSummary,
    className?: string,
  ) => ReactNode;
}

export function TicketListContent({
  isLoading,
  ticketsError,
  tickets,
  selectedTicketId,
  isAdminVariant,
  dateLocale,
  text,
  onRetry,
  onSelectTicket,
  renderTicketOwnership,
}: TicketListContentProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoaderCircle className="h-5 w-5 animate-spin text-gray-500" />
      </div>
    );
  }

  if (ticketsError) {
    const status = getStatusFromError(ticketsError);
    const isForbidden = status === 401 || status === 403;

    return (
      <div className="space-y-3 rounded-md border border-red-100 bg-red-50 p-4 text-sm text-red-700">
        <p>
          {isForbidden
            ? text.noPermissionToViewTickets
            : text.ticketListLoadFailed}
        </p>
        <Button type="button" size="sm" variant="outline" onClick={onRetry}>
          {text.retry}
        </Button>
      </div>
    );
  }

  if (tickets.length === 0) {
    return (
      <div className="rounded-md border border-dashed py-8 text-center text-sm text-gray-500">
        {text.noTicketsFound}
      </div>
    );
  }

  return (
    <ul className="space-y-2">
      {tickets.map((ticket) => (
        <li key={ticket.id}>
          <button
            type="button"
            onClick={() => onSelectTicket(ticket.id)}
            className={cn(
              'w-full rounded-md border px-3 py-3 text-left transition-colors',
              selectedTicketId === ticket.id
                ? 'border-black bg-gray-50'
                : 'border-gray-200 bg-white hover:border-gray-300',
            )}
          >
            <div className="mb-2 flex items-start justify-between gap-2">
              <p className="line-clamp-2 text-sm font-medium text-gray-800">
                {ticket.subject}
              </p>
              <div className="flex flex-wrap items-center justify-end gap-1">
                <Badge
                  variant="outline"
                  className={cn(
                    'whitespace-nowrap',
                    getPriorityClassName(ticket.priority),
                  )}
                >
                  {getPriorityLabel(ticket.priority, text.priorityLabels)}
                </Badge>
                <Badge
                  variant="outline"
                  className={cn(
                    'whitespace-nowrap',
                    getStatusClassName(ticket.status),
                  )}
                >
                  {getStatusLabel(ticket.status, text.statusLabels)}
                </Badge>
              </div>
            </div>

            <p className="text-xs text-gray-500">
              {text.ticketUpdatedAtLabel(
                ticket.id,
                getFormattedDate(ticket.updatedAt, dateLocale),
              )}
            </p>

            {isAdminVariant && renderTicketOwnership(ticket, 'mt-1')}

            {ticket.awaitingRootReply && (
              <p className="mt-1 text-xs text-amber-700">
                {text.awaitingSupportReply}
              </p>
            )}
          </button>
        </li>
      ))}
    </ul>
  );
}
